import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import {
  X,
  Search,
  Edit2,
  Key,
  Package,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Building2,
  Save,
  AlertCircle,
  Lock,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';
import API_BASE_URL from '../config';

export default function LicenseManagement({ setCurrentPage }) {
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPageNum] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [clients, setClients] = useState([]);
  const [usersByClient, setUsersByClient] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    isTaken: false,
    subscriptionEnd: null
  });
  const itemsPerPage = 6;

  // Password Protection States
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const CORRECT_PASSWORD = 'QSIT';

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLicenses();
    }
  }, [isAuthenticated]);

  const fetchLicenses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/LicenseKey`, {
        headers: { 'accept': 'text/plain' }
      });

      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const apiLicenses = await response.json();
      const apiKeys = apiLicenses
        .filter(item => item.isActive)
        .map(item => item.licenseKeyValue);

      const ref = collection(db, "services");
      const snapshot = await getDocs(ref);

      const filtered = snapshot.docs
        .filter(doc => apiKeys.includes(doc.id))
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          hasLicense: true,
        }));

      setServices(filtered);
    } catch (err) {
      console.error("Error loading licenses:", err);
      alert('Failed to load license keys. Please check if server is running.');
      setServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewService = async (service) => {
    setSelectedService(service);
    setShowEditModal(true);
    setClients([]);
    setUsersByClient({});
    setIsLoading(true);

    try {
      const clientsRef = collection(db, "services", service.id, "clients");
      const clientSnap = await getDocs(clientsRef);
      const clientList = clientSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientList);

      const usersMap = {};
      for (const client of clientList) {
        const usersRef = collection(db, "services", service.id, "clients", client.id, "users");
        const userSnap = await getDocs(usersRef);
        usersMap[client.id] = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      setUsersByClient(usersMap);
    } catch (err) {
      console.error("Error loading clients and users:", err);
      alert('Failed to load license details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (user, clientId) => {
    setEditingUser({ ...user, clientId });
    setEditFormData({
      isTaken: user.isTaken || false,
      subscriptionEnd: user.subscriptionEnd?.seconds
        ? new Date(user.subscriptionEnd.seconds * 1000).toISOString().split('T')[0]
        : ''
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser || !selectedService) return;

    try {
      const userRef = doc(
        db,
        "services",
        selectedService.id,
        "clients",
        editingUser.clientId,
        "users",
        editingUser.id
      );

      const updateData = {
        isTaken: editFormData.isTaken
      };

      if (editFormData.subscriptionEnd) {
        updateData.subscriptionEnd = new Date(editFormData.subscriptionEnd);
      }

      await updateDoc(userRef, updateData);

      // Refresh data
      await handleViewService(selectedService);
      
      setEditingUser(null);
      alert('License status updated successfully!');
    } catch (error) {
      console.error("Failed to update license:", error);
      alert('Failed to update license. Please try again.');
    }
  };

  const getAllUsers = () => {
    const allUsers = [];
    clients.forEach(client => {
      const users = usersByClient[client.id] || [];
      users.forEach(user => {
        allUsers.push({
          ...user,
          clientName: client.name,
          clientId: client.id
        });
      });
    });
    return allUsers;
  };

  const filteredServices = services.filter(service =>
    (service.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (service.id?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedServices = filteredServices.slice(startIndex, startIndex + itemsPerPage);

  const getServiceStats = (service) => {
    // This would need to be calculated after fetching clients/users
    return {
      totalUsers: 0,
      takenCount: 0,
      availableCount: 0
    };
  };

  return (
    <div className="p-6">
      {/* Password Protection Screen */}
      {!isAuthenticated ? (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
          <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full border-2 border-blue-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
                <Shield size={40} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Protected Access</h1>
              <p className="text-gray-600">Enter password to access License Management</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError('');
                    }}
                    className={`w-full pl-12 pr-12 py-4 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                      passwordError 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {passwordError && (
                  <div className="flex items-center gap-2 mt-2 text-red-600">
                    <AlertCircle size={16} />
                    <p className="text-sm font-medium">{passwordError}</p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Access License Management
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setCurrentPage('dashboard')}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Main License Management Content */
        <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">License Management</h1>
          <p className="text-slate-600">View and manage all license keys and their status</p>
        </div>
        <button
          onClick={() => setIsAuthenticated(false)}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
        >
          <Lock size={18} />
          Lock
        </button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search licenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {isLoading ? (
          <div className="col-span-full text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading licenses...</p>
          </div>
        ) : paginatedServices.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Package size={56} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No licenses found</p>
          </div>
        ) : (
          paginatedServices.map((service) => (
            <div
              key={service.id}
              className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-blue-200 hover:border-blue-400 hover:-translate-y-1"
            >
              <div className="bg-gradient-to-r from-blue-600 to-blue-600 p-6 text-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Key className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    ● Active
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-1">{service.name || "License Key"}</h3>
                <p className="text-blue-100 text-sm font-mono truncate">{service.id}</p>
              </div>

              <div className="p-6 space-y-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Created: {formatDate(service.createdAt)}</span>
                </div>

                <div className="pt-3 border-t border-slate-200">
                  <button
                    onClick={() => handleViewService(service)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-700 transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    View & Edit
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4">
          <button
            onClick={() => setCurrentPageNum(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="p-2 bg-white rounded-lg shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex gap-2">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPageNum(i + 1)}
                className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                  currentPage === i + 1
                    ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPageNum(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="p-2 bg-white rounded-lg shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedService && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Package size={32} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-2">{selectedService.name || "License Key"}</h2>
                    <p className="text-blue-100 font-mono text-sm">{selectedService.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedService(null);
                    setEditingUser(null);
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 size={20} />
                    <span className="text-white/80 text-sm">Clients</span>
                  </div>
                  <p className="text-3xl font-bold">{clients.length}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={20} />
                    <span className="text-white/80 text-sm">Total Users</span>
                  </div>
                  <p className="text-3xl font-bold">{getAllUsers().length}</p>
                </div>
                <div className="bg-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={20} />
                    <span className="text-white/80 text-sm">Taken</span>
                  </div>
                  <p className="text-3xl font-bold">{getAllUsers().filter(u => u.isTaken).length}</p>
                </div>
                <div className="bg-yellow-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={20} />
                    <span className="text-white/80 text-sm">Available</span>
                  </div>
                  <p className="text-3xl font-bold">{getAllUsers().filter(u => !u.isTaken).length}</p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-280px)]">
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading license details...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {clients.map(client => {
                    const clientUsers = usersByClient[client.id] || [];
                    if (clientUsers.length === 0) return null;

                    return (
                      <div key={client.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                            <Building2 size={24} className="text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{client.name || "Client"}</h3>
                            <p className="text-sm text-gray-600">{clientUsers.length} user(s)</p>
                          </div>
                        </div>

                        {/* Users Table */}
                        <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-lg">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-100 border-b-2 border-gray-200">
                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">User Code</th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">License Key</th>
                                <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">Expiration</th>
                                <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">Status</th>
                                <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientUsers.map(user => (
                                <tr key={user.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                  <td className="px-6 py-4">
                                    <span className="font-mono text-sm font-semibold text-gray-900">
                                      {user.userCode || "N/A"}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="font-mono text-sm text-gray-700">
                                      {user.licenseKey || selectedService.id}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    {editingUser?.id === user.id ? (
                                      <input
                                        type="date"
                                        value={editFormData.subscriptionEnd}
                                        onChange={(e) => setEditFormData({
                                          ...editFormData,
                                          subscriptionEnd: e.target.value
                                        })}
                                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                                      />
                                    ) : (
                                      <span className="text-sm text-gray-700">
                                        {formatDate(user.subscriptionEnd)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    {editingUser?.id === user.id ? (
                                      <select
                                        value={editFormData.isTaken ? "taken" : "available"}
                                        onChange={(e) => setEditFormData({
                                          ...editFormData,
                                          isTaken: e.target.value === "taken"
                                        })}
                                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                                      >
                                        <option value="available">Available</option>
                                        <option value="taken">Taken</option>
                                      </select>
                                    ) : user.isTaken ? (
                                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                                        <XCircle size={14} />
                                        Taken
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                                        <CheckCircle size={14} />
                                        Available
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    {editingUser?.id === user.id ? (
                                      <div className="flex gap-2 justify-center">
                                        <button
                                          onClick={handleSaveUser}
                                          className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-700 transition-all shadow-lg flex items-center gap-2"
                                        >
                                          <Save size={16} />
                                          Save
                                        </button>
                                        <button
                                          onClick={() => setEditingUser(null)}
                                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => handleEditUser(user, client.id)}
                                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-700 transition-all shadow-lg flex items-center gap-2 mx-auto"
                                      >
                                        <Edit2 size={16} />
                                        Edit
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}